/*
 * ******************************************************************
 * Copyright (c) 2021-2026 Broadcom. All Rights Reserved.
 * Broadcom Confidential. The term "Broadcom" refers to Broadcom Inc.
 * and/or its subsidiaries.
 * ******************************************************************
 */
import { APP_ROUTES_UTIL } from "@app/app-routing.util";
import { ServiceTypeEnum, VcdsPersonaEnum } from "@api/generated/vcdr";

export class DASHBOARD {
    public static readonly ROOT: string = "dashboard";
}

export class ORGANIZATIONS {
    public static readonly ROOT: string = "organizations";
    public static readonly ID: string = "orgId";
    public static readonly ADD: string = "add";
    public static readonly SUMMARY: string = "summary";
    public static readonly PROPERTIES: string = "properties";
    public static readonly CAPABILITIES: string = "capabilities";
    public static readonly DEPLOYMENTS: string = "deployments";
    public static readonly SUBSCRIPTIONS: string = "subscriptions";
    public static readonly RESOURCES: string = "resources";
    public static readonly RWR_DEPLOYMENTS: string = "rwr-deployments";
    public static readonly SETTINGS: string = "settings";
    public static readonly TASKS: string = "tasks";
    public static readonly EVENTS: string = "events";
}

export class SUBSCRIPTIONS {
    public static readonly ROOT: string = "subscriptions";
    public static readonly LIST: string = "list";
    public static readonly OFFLINE_LICENSES: string = "offline-licenses";
    public static readonly ENFORCEMENTS: string = "enforcements";
    public static readonly USAGE: string = "usage";
    public static readonly SETUP: string = "create-subscription";
    public static readonly SETUP_ONDEMAND: string = "create-subscription-on-demand";

}

export class TRENDS {
    public static readonly ROOT: string = "trends";
    public static readonly SUBSCRIPTIONS: string = "subscriptions";
    public static readonly USAGE: string = "usage";
    public static readonly VCDR: string = "vcdr";
    public static readonly VMCFS: string = "vmcfs";
    public static readonly VMC: string = "vmc";
}

export class DEPLOYMENTS {
    public static readonly ROOT: string = "deployments";
    public static readonly ID: string = "deploymentId";
    public static readonly SUMMARY: string = "summary";
    public static readonly COMPONENTS: string = "components";
    public static readonly PROPERTIES: string = "properties";
    public static readonly SETUP: string = "deploy-instance";
    public static readonly IMPORT: string = "import-instance";
    public static readonly SETUP_CLEAN_ROOM: string = "deploy-clean-room";
}

export class COMPONENTS {
    public static readonly ROOT: string = "components";
    public static readonly ID: string = "componentId";
    public static readonly SUMMARY: string = "summary";
    public static readonly PROPERTIES: string = "properties";
}

export class REQUESTS {
    public static readonly ROOT: string = "requests";
}

export class RESOURCES {
    public static readonly ROOT: string = "resources";
}

export class OAUTH_APPS {
    public static readonly ROOT: string = "oauth-apps";
}

export class RWR_DEPLOYMENTS {
    public static readonly ROOT: string = "rwr-deployments";
}

export class CLOUD_COST_MGMT {
    public static readonly ROOT: string = "cloud-cost-mgmt";
    public static readonly ACCOUNT_ID: string = "accountId";
    public static readonly SERVICES: string = "services";
    public static readonly LEAKED_RESOURCES: string = "leaked-resources";
}

export class TASKS {
    public static readonly ROOT: string = "tasks";
}

export class EVENTS {
    public static readonly ROOT: string = "events";
}

export class COMPOSE_EMAIL {
    public static readonly ROOT: string = "compose-email";
}

export class ROLLOUTS {
    public static readonly ROOT: string = "rollouts";
    public static readonly PLAN_SETUP: string = "create-rollout-plan";
    public static readonly PLAN_EDIT: string = "edit";
    public static readonly PLAN: string = "rollout-plan";
    public static readonly PLAN_ID: string = "rolloutId";
    public static readonly WAVES: string = "waves";
    public static readonly WAVE_ID: string = "waveId";
    public static readonly WAVE_SETUP: string = "create-rollout-wave";
    public static readonly WAVE_EDIT: string = "edit-rollout-wave";
    public static readonly WAVE_EDIT_ORG_TARGETS: string = "edit-rollout-wave-org-targets";
    public static readonly WAVE_EDIT_DEPLOYMENT_TARGETS: string = "edit-rollout-wave-depl-targets";
    public static readonly NOTIFICATION_EDIT: string = "edit-notification";
    public static readonly NOTIFICATIONS: string = "notifications";
    public static readonly NOTIFICATION_CREATE: string = "create-notification";
    public static readonly NOTIFICATION_NAME: string = "notification-name";
}

export class SCHEDULED_JOBS {
    public static readonly ROOT: string = "scheduled-jobs";
    public static readonly LIST: string = "list";
    public static readonly HISTORY: string = "history";
}

export class OPERATOR_CONSOLE {
    public static readonly EMPTY: string = "";
    public static readonly ROOT: string = "operator";
    public static readonly ROOT_ABSOLUTE: string =
        APP_ROUTES_UTIL.absolutePath(OPERATOR_CONSOLE.EMPTY, OPERATOR_CONSOLE.ROOT);
    public static readonly DASHBOARD_ABSOLUTE = OPERATOR_CONSOLE.ROOT_ABSOLUTE + DASHBOARD.ROOT;
    public static readonly DEPLOYMENTS_ABSOLUTE = OPERATOR_CONSOLE.ROOT_ABSOLUTE + DEPLOYMENTS.ROOT;
    public static readonly SUBSCRIPTIONS_ABSOLUTE = OPERATOR_CONSOLE.ROOT_ABSOLUTE + SUBSCRIPTIONS.ROOT;
    public static readonly SUBSCRIPTIONS_ABSOLUTE_LIST = OPERATOR_CONSOLE.SUBSCRIPTIONS_ABSOLUTE + APP_ROUTES_UTIL.SLASH + SUBSCRIPTIONS.LIST;
    public static readonly SUBSCRIPTIONS_ABSOLUTE_OFFLINE_LICENSE = OPERATOR_CONSOLE.SUBSCRIPTIONS_ABSOLUTE + APP_ROUTES_UTIL.SLASH + SUBSCRIPTIONS.OFFLINE_LICENSES;
    public static readonly SUBSCRIPTIONS_ABSOLUTE_ENFORCEMENTS = OPERATOR_CONSOLE.SUBSCRIPTIONS_ABSOLUTE + APP_ROUTES_UTIL.SLASH + SUBSCRIPTIONS.ENFORCEMENTS;
    public static readonly ORGANIZATIONS_ABSOLUTE = OPERATOR_CONSOLE.ROOT_ABSOLUTE + ORGANIZATIONS.ROOT;
    public static readonly TRENDS_ABSOLUTE = OPERATOR_CONSOLE.ROOT_ABSOLUTE + TRENDS.ROOT;
    public static readonly TRENDS_ABSOLUTE_SUBSCRIPTIONS = OPERATOR_CONSOLE.TRENDS_ABSOLUTE + APP_ROUTES_UTIL.SLASH + TRENDS.SUBSCRIPTIONS;
    public static readonly TRENDS_ABSOLUTE_USAGE = OPERATOR_CONSOLE.TRENDS_ABSOLUTE + APP_ROUTES_UTIL.SLASH + TRENDS.USAGE;
    public static readonly TASKS_ABSOLUTE = OPERATOR_CONSOLE.ROOT_ABSOLUTE + TASKS.ROOT;
    public static readonly EVENTS_ABSOLUTE = OPERATOR_CONSOLE.ROOT_ABSOLUTE + EVENTS.ROOT;
    public static readonly SCHEDULED_JOBS_ABSOLUTE = OPERATOR_CONSOLE.ROOT_ABSOLUTE + SCHEDULED_JOBS.ROOT;
    public static readonly SCHEDULED_JOBS_ACTIVE_ABSOLUTE = OPERATOR_CONSOLE.ROOT_ABSOLUTE + SCHEDULED_JOBS.ROOT + SCHEDULED_JOBS.LIST;
    public static readonly SCHEDULED_JOBS_HISTORY_ABSOLUTE = OPERATOR_CONSOLE.ROOT_ABSOLUTE + SCHEDULED_JOBS.ROOT + SCHEDULED_JOBS.HISTORY;
    public static readonly ROLLOUTS_ABSOLUTE = OPERATOR_CONSOLE.ROOT_ABSOLUTE + ROLLOUTS.ROOT;
    public static readonly ROLLOUT_NOTIFICATIONS_ABSOLUTE = OPERATOR_CONSOLE.ROOT_ABSOLUTE + ROLLOUTS.NOTIFICATIONS;
    public static readonly ROLLOUT_PLAN_SETUP_ABSOLUTE = OPERATOR_CONSOLE.ROOT_ABSOLUTE + ROLLOUTS.PLAN_SETUP;
    public static readonly REQUESTS_ABSOLUTE = OPERATOR_CONSOLE.ROOT_ABSOLUTE + REQUESTS.ROOT;
    public static readonly RESOURCES_ABSOLUTE = OPERATOR_CONSOLE.ROOT_ABSOLUTE + RESOURCES.ROOT;
    public static readonly OAUTH_APPS_ABSOLUTE = OPERATOR_CONSOLE.ROOT_ABSOLUTE + OAUTH_APPS.ROOT;
    public static readonly RWR_DEPLOYMENTS_ABSOLUTE = OPERATOR_CONSOLE.ROOT_ABSOLUTE + RWR_DEPLOYMENTS.ROOT;
    public static readonly CLOUD_COST_MGMT_ABSOLUTE = OPERATOR_CONSOLE.ROOT_ABSOLUTE + CLOUD_COST_MGMT.ROOT;
    public static readonly SUBSCRIPTIONS_SETUP_ABSOLUTE = OPERATOR_CONSOLE.ROOT_ABSOLUTE + SUBSCRIPTIONS.SETUP;
    public static readonly SUBSCRIPTIONS_SETUP_ONDEMAND_ABSOLUTE = OPERATOR_CONSOLE.ROOT_ABSOLUTE + SUBSCRIPTIONS.SETUP_ONDEMAND;
    public static readonly SERVICE_TYPE: string = "service-type";
    public static readonly INSTANCES_SETUP_ABSOLUTE = OPERATOR_CONSOLE.ROOT_ABSOLUTE + DEPLOYMENTS.SETUP;
    public static readonly INSTANCES_IMPORT_ABSOLUTE = OPERATOR_CONSOLE.ROOT_ABSOLUTE + DEPLOYMENTS.IMPORT;
    public static readonly INSTANCES_SETUP_CLEAN_ROOM_ABSOLUTE = OPERATOR_CONSOLE.ROOT_ABSOLUTE + DEPLOYMENTS.SETUP_CLEAN_ROOM;
    public static readonly ROLLOUT_NOTIFICATIONS_CREATE_ABSOLUTE = OPERATOR_CONSOLE.ROOT_ABSOLUTE + ROLLOUTS.NOTIFICATION_CREATE;
    public static readonly COMPOSE_EMAIL_ABSOLUTE = OPERATOR_CONSOLE.ROOT_ABSOLUTE + COMPOSE_EMAIL.ROOT;

    public static orgAbsolute(orgId: string, tab?: string): string {
        if (!orgId) {
            return OPERATOR_CONSOLE.ORGANIZATIONS_ABSOLUTE;
        }
        if (tab) {
            return OPERATOR_CONSOLE.ORGANIZATIONS_ABSOLUTE + APP_ROUTES_UTIL.SLASH + orgId + APP_ROUTES_UTIL.SLASH + tab;
        }
        return OPERATOR_CONSOLE.ORGANIZATIONS_ABSOLUTE + APP_ROUTES_UTIL.SLASH + orgId;
    }

    public static instanceSetupAbsolute(orgId: string,
                                        persona: VcdsPersonaEnum,
                                        setupRegionPath: string = OPERATOR_CONSOLE.INSTANCES_SETUP_ABSOLUTE): string {
        if (!orgId) {
            return setupRegionPath;
        }
        if (!persona) {
            return setupRegionPath + APP_ROUTES_UTIL.SLASH + orgId;
        }
        const serviceType = persona === VcdsPersonaEnum.VMCFS ? ServiceTypeEnum.VMCFS : ServiceTypeEnum.VCDR;
        return setupRegionPath + APP_ROUTES_UTIL.SLASH + orgId + APP_ROUTES_UTIL.SLASH + serviceType.toLowerCase();
    }

    public static subscriptionsSetupAbsolute(orgId: string, serviceType: ServiceTypeEnum): string {
        if (!serviceType) {
            return OPERATOR_CONSOLE.SUBSCRIPTIONS_SETUP_ABSOLUTE;
        }
        return OPERATOR_CONSOLE.SUBSCRIPTIONS_SETUP_ABSOLUTE + APP_ROUTES_UTIL.SLASH + orgId + APP_ROUTES_UTIL.SLASH + serviceType.toLowerCase();
    }

    public static subscriptionsSetupOnDemandAbsolute(orgId: string, serviceType: ServiceTypeEnum): string {
        if (!serviceType) {
            return OPERATOR_CONSOLE.SUBSCRIPTIONS_SETUP_ONDEMAND_ABSOLUTE;
        }
        return OPERATOR_CONSOLE.SUBSCRIPTIONS_SETUP_ONDEMAND_ABSOLUTE + APP_ROUTES_UTIL.SLASH + orgId + APP_ROUTES_UTIL.SLASH + serviceType.toLowerCase();
    }

    public static deploymentRelative(deploymentId: string, tab?: string): string {
        if (tab) {
            return DEPLOYMENTS.ROOT + APP_ROUTES_UTIL.SLASH + deploymentId + APP_ROUTES_UTIL.SLASH + tab;
        }
        return DEPLOYMENTS.ROOT + APP_ROUTES_UTIL.SLASH + deploymentId;
    }

    public static deploymentsAbsolute(orgId?: string): string {
        const deploymentsRoot = orgId ?
                                OPERATOR_CONSOLE.ORGANIZATIONS_ABSOLUTE + APP_ROUTES_UTIL.SLASH + orgId + APP_ROUTES_UTIL.SLASH :
                                OPERATOR_CONSOLE.ROOT_ABSOLUTE;
        return deploymentsRoot + DEPLOYMENTS.ROOT;
    }

    public static deploymentAbsolute(deploymentId: string, tab?: string): string {
        if (!deploymentId) {
            return OPERATOR_CONSOLE.DEPLOYMENTS_ABSOLUTE;
        }
        return OPERATOR_CONSOLE.ROOT_ABSOLUTE + this.deploymentRelative(deploymentId, tab);
    }

    public static componentsAbsolute(deploymentId: string, orgId?: string): string {
        return this.deploymentsAbsolute(orgId) + APP_ROUTES_UTIL.SLASH + deploymentId +
            APP_ROUTES_UTIL.SLASH + COMPONENTS.ROOT;
    }

    public static componentAbsolute(deploymentId: string, componentId: string, tab?: string): string {
        if (tab) {
            return this.deploymentAbsolute(deploymentId,
                                           COMPONENTS.ROOT) + APP_ROUTES_UTIL.SLASH + componentId + APP_ROUTES_UTIL.SLASH + tab;
        }
        return this.deploymentAbsolute(deploymentId, COMPONENTS.ROOT) + APP_ROUTES_UTIL.SLASH + componentId;
    }

    public static rolloutAbsolute(planId: string, waveId?: string): string {
        let route = OPERATOR_CONSOLE.ROLLOUTS_ABSOLUTE + APP_ROUTES_UTIL.SLASH + planId;
        if (waveId) {
            route += APP_ROUTES_UTIL.SLASH + ROLLOUTS.WAVES + APP_ROUTES_UTIL.SLASH + waveId;
        }
        return route;
    }

    public static rolloutEditAbsolute(planId: string): string {
        return OPERATOR_CONSOLE.ROLLOUTS_ABSOLUTE + APP_ROUTES_UTIL.SLASH + planId + APP_ROUTES_UTIL.SLASH + ROLLOUTS.PLAN_EDIT;
    }

    public static rolloutWaveSetupAbsolute(planId: string): string {
        return OPERATOR_CONSOLE.ROLLOUTS_ABSOLUTE + APP_ROUTES_UTIL.SLASH + planId + APP_ROUTES_UTIL.SLASH + ROLLOUTS.WAVE_SETUP;
    }

    public static rolloutPlanNotificationSetupAbsolute(planId: string): string {
        return OPERATOR_CONSOLE.ROLLOUTS_ABSOLUTE + APP_ROUTES_UTIL.SLASH + planId + APP_ROUTES_UTIL.SLASH + ROLLOUTS.NOTIFICATION_EDIT;
    }

    public static rolloutWaveNotificationSetupAbsolute(planId: string, waveId: string): string {
        return OPERATOR_CONSOLE.ROLLOUTS_ABSOLUTE + APP_ROUTES_UTIL.SLASH + planId + APP_ROUTES_UTIL.SLASH +
            ROLLOUTS.WAVES + APP_ROUTES_UTIL.SLASH + waveId + APP_ROUTES_UTIL.SLASH + ROLLOUTS.NOTIFICATION_EDIT;
    }

    public static rolloutWaveEditAbsolute(planId: string, waveId: string): string {
        return OPERATOR_CONSOLE.ROLLOUTS_ABSOLUTE + APP_ROUTES_UTIL.SLASH + planId + APP_ROUTES_UTIL.SLASH +
            ROLLOUTS.WAVES + APP_ROUTES_UTIL.SLASH + waveId + APP_ROUTES_UTIL.SLASH + ROLLOUTS.WAVE_EDIT;
    }

    public static rolloutAddWaveOrgTargetsAbsolute(planId: string, waveId: string): string {
        return OPERATOR_CONSOLE.ROLLOUTS_ABSOLUTE + APP_ROUTES_UTIL.SLASH + planId + APP_ROUTES_UTIL.SLASH +
            ROLLOUTS.WAVES + APP_ROUTES_UTIL.SLASH + waveId + APP_ROUTES_UTIL.SLASH + ROLLOUTS.WAVE_EDIT_ORG_TARGETS;
    }

    public static rolloutAddWaveDeploymentTargetsAbsolute(planId: string, waveId: string): string {
        return OPERATOR_CONSOLE.ROLLOUTS_ABSOLUTE + APP_ROUTES_UTIL.SLASH + planId + APP_ROUTES_UTIL.SLASH +
            ROLLOUTS.WAVES + APP_ROUTES_UTIL.SLASH + waveId + APP_ROUTES_UTIL.SLASH + ROLLOUTS.WAVE_EDIT_DEPLOYMENT_TARGETS;
    }
}
